import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { dimensionLabel, dimensionShort } from "../lib/scoring";
import MarkButton from "./MarkButton";

/**
 * Gate 7 — Divergence view.
 *
 * Surfaces items where the cohort's rank-by-mean and rank-by-conviction
 * (top of the SCORED scale) differ by ≥2 positions. The pattern reveals
 * "compliance without conviction" (everyone says Agree, almost nobody
 * says Strongly Agree) and the reverse.
 *
 * One backend call: GET /api/deployments/:id/divergence?filters=...
 * n<5 suppression applies wire-side — payload carries no item-level data
 * if the cohort is below threshold, mirroring /scores and /heatmap.
 */
export default function Divergence({ deploymentId, filters }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError("");
      try {
        const params = Object.keys(filters || {}).length
          ? { filters: JSON.stringify(filters) }
          : undefined;
        const { data: payload } = await api.get(
          `/api/deployments/${deploymentId}/divergence`,
          { params },
        );
        if (!cancelled) setData(payload);
      } catch (e) {
        if (!cancelled) setError(formatApiError(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [deploymentId, filters]);

  const { compliance, conviction } = useMemo(() => {
    if (!data?.items) return { compliance: [], conviction: [] };
    const flagged = data.items.filter((i) => i.flagged);
    return {
      // Largest positive delta first — most-compliant on top.
      compliance: flagged
        .filter((i) => i.direction === "compliance_without_conviction")
        .sort((a, b) => b.rank_delta - a.rank_delta),
      // Largest negative delta first — most-convicted on top.
      conviction: flagged
        .filter((i) => i.direction === "conviction_without_compliance")
        .sort((a, b) => a.rank_delta - b.rank_delta),
    };
  }, [data]);

  if (loading) {
    return <div className="text-sm text-gray-500" data-testid="divergence-loading">Loading…</div>;
  }
  if (error) {
    return <div className="text-sm" style={{ color: "#AD393E" }} data-testid="divergence-error">{error}</div>;
  }
  if (!data) return null;

  if (data.suppressed) {
    return (
      <div className="panel" data-testid="divergence-suppressed">
        <div className="eyebrow mb-2" style={{ color: "#AD393E" }}>Suppressed</div>
        <p className="text-sm text-gray-700">
          Only {data.n} respondent{data.n === 1 ? "" : "s"} in this cohort. Divergence
          analysis becomes available at five completed respondents.
        </p>
      </div>
    );
  }

  return (
    <div data-testid="divergence">
      <DivergenceHeader n={data.n} flaggedCount={data.flagged_count} />

      {compliance.length === 0 && conviction.length === 0 && (
        <EmptyState />
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 mt-8">
        <DivergenceColumn
          testId="compliance-column"
          eyebrow="Compliance without conviction"
          subtitle="High mean — but few are convicted."
          interpretation="People are agreeing without being convicted. The number reads well; the room may not be behind it."
          items={compliance}
          deltaSign="+"
          deltaColor="#AD393E"
        />
        <DivergenceColumn
          testId="conviction-column"
          eyebrow="Conviction without compliance"
          subtitle="Low mean — but those who are convicted, are."
          interpretation="The cohort overall is soft on this — but a real fraction holds strong feeling on it. The signal lives in the minority."
          items={conviction}
          deltaSign="−"
          deltaColor="#4592A8"
        />
      </div>
    </div>
  );
}

function DivergenceHeader({ n, flaggedCount }) {
  return (
    <div data-testid="divergence-header">
      <div className="eyebrow mb-1">Divergence</div>
      <h3 className="text-lg mb-3" style={{ fontFamily: "Inter", fontWeight: 500 }}>
        Where average and conviction don&rsquo;t agree.
      </h3>
      <p className="text-sm text-gray-700 max-w-prose">
        When ranking items by mean and by conviction (the share of responses at the
        top of the scale) diverges by two positions or more, the gap between
        agreement and conviction is real signal — and worth a conversation. Cohort
        of n = {n}. {flaggedCount} item{flaggedCount === 1 ? "" : "s"} flagged.
      </p>
    </div>
  );
}

function EmptyState() {
  return (
    <div className="panel mt-8" data-testid="divergence-empty">
      <div className="eyebrow mb-2" style={{ color: "#2F6B7C" }}>Aligned</div>
      <p className="text-sm text-gray-700 max-w-prose">
        No divergent items in this cohort. Mean and conviction rankings agree on
        where the work is.
      </p>
    </div>
  );
}

function DivergenceColumn({ testId, eyebrow, subtitle, interpretation, items, deltaSign, deltaColor }) {
  return (
    <section data-testid={testId}>
      <div className="eyebrow mb-1" style={{ color: deltaColor }}>{eyebrow}</div>
      <div className="text-sm text-ink mb-2" style={{ fontFamily: "Inter", fontWeight: 500 }}>
        {subtitle}
      </div>
      <p className="text-xs text-gray-600 mb-6 max-w-prose">{interpretation}</p>
      {items.length === 0 ? (
        <div className="text-sm text-gray-500" data-testid={`${testId}-empty`}>
          Nothing in this direction.
        </div>
      ) : (
        <ol className="space-y-4">
          {items.map((it) => (
            <ItemRow key={it.item} item={it} deltaSign={deltaSign} deltaColor={deltaColor} />
          ))}
        </ol>
      )}
    </section>
  );
}

function ItemRow({ item, deltaSign, deltaColor }) {
  return (
    <li
      className="border border-gray-100 bg-paper p-4"
      data-testid={`divergence-item-${item.item}`}
    >
      <div className="flex items-baseline justify-between gap-4 mb-2">
        <div className="flex items-baseline gap-3 min-w-0">
          <span
            className="text-xs text-gray-500 shrink-0"
            style={{ fontFamily: "Oswald", letterSpacing: "0.1em" }}
          >
            {String(item.item).padStart(2, "0")}
          </span>
          <span
            className="text-xs uppercase tracking-wider shrink-0"
            style={{ fontFamily: "Oswald", color: "#2F6B7C" }}
          >
            {dimensionShort(item.dimension)}
          </span>
        </div>
        <div
          className="text-sm shrink-0"
          style={{ fontFamily: "Oswald", color: deltaColor, fontWeight: 600 }}
          data-testid={`divergence-item-${item.item}-delta`}
        >
          Δ {deltaSign}
          {Math.abs(item.rank_delta)}
        </div>
      </div>
      <p
        className="text-sm text-ink leading-snug mb-3"
        style={{ fontFamily: "Inter" }}
      >
        {item.text}
      </p>
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-6 text-xs flex-wrap">
          <RankPill label="mean" value={item.scored_mean.toFixed(2)} rank={item.mean_rank} />
          <RankPill
            label="conviction"
            value={`${Math.round(item.conviction_pct)}%`}
            rank={item.tb_rank}
          />
        </div>
        <MarkButton
          kind="item"
          payload={{ item_number: item.item }}
          testIdSuffix={`divergence-${item.item}`}
        />
      </div>
    </li>
  );
}

function RankPill({ label, value, rank }) {
  return (
    <div className="flex items-baseline gap-2">
      <span className="uppercase tracking-wider text-gray-500" style={{ fontFamily: "Oswald" }}>
        {label}
      </span>
      <span style={{ fontFamily: "Oswald", fontWeight: 600, color: "#1A1A1A" }}>
        {value}
      </span>
      <span className="text-gray-400">·</span>
      <span className="text-gray-500">rank {rank} / 56</span>
    </div>
  );
}
